import React, { createContext, useContext, useState, useEffect } from "react";
import { RepairPart, RepairPartFormData } from "@/types/stock";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useRepairShopContext } from "@/context/RepairShopContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface RepairPartsContextType {
  repairParts: RepairPart[];
  loading: boolean;

  // CRUD operations
  addRepairPart: (data: RepairPartFormData) => Promise<RepairPart>;
  updateRepairPart: (id: string, data: Partial<RepairPartFormData>) => Promise<void>;
  deleteRepairPart: (id: string) => Promise<void>;
  getRepairPartById: (id: string) => Promise<RepairPart | undefined>;

  // Stock operations
  adjustStock: (partId: string, quantity: number, reason: string) => Promise<void>;

  // Refresh function
  refreshRepairParts: () => Promise<void>;
}

const RepairPartsContext = createContext<RepairPartsContextType | undefined>(undefined);

export const RepairPartsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { repairShop } = useRepairShopContext();
  const { t } = useTranslation();

  const [repairParts, setRepairParts] = useState<RepairPart[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch repair parts
  const refreshRepairParts = async () => {
    if (!user || !repairShop) return;

    try {
      const { data, error } = await supabase
        .from("repair_parts")
        .select("*")
        .eq("repair_shop_id", repairShop.id)
        .order("name", { ascending: true });

      if (error) throw error;

      const processedParts: RepairPart[] = (data || []).map((part: any) => ({
        id: part.id,
        name: part.name,
        description: part.description || undefined,
        cost: part.cost,
        stockQuantity: part.stock_quantity,
        minStockLevel: part.min_stock_level,
        isActive: part.is_active,
        repairShopId: part.repair_shop_id,
        createdAt: new Date(part.created_at),
        updatedAt: new Date(part.updated_at),
      }));

      setRepairParts(processedParts);
    } catch (error) {
      console.error("Error fetching repair parts:", error);
      toast.error(t("stock.fetchError"));
    }
  };

  // Initial load
  useEffect(() => {
    if (user && repairShop) {
      refreshRepairParts().finally(() => setLoading(false));
    }
  }, [user, repairShop]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user || !repairShop) return;

    const subscription = supabase
      .channel("repair-parts-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "repair_parts",
          filter: `repair_shop_id=eq.${repairShop.id}`,
        },
        () => refreshRepairParts()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [user, repairShop]);

  const addRepairPart = async (data: RepairPartFormData): Promise<RepairPart> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      const { data: insertedData, error } = await supabase
        .from("repair_parts")
        .insert({
          name: data.name,
          description: data.description,
          cost: data.cost,
          stock_quantity: data.stockQuantity,
          min_stock_level: data.minStockLevel,
          is_active: data.isActive,
          repair_shop_id: repairShop.id,
        })
        .select()
        .single();

      if (error) throw error;

      await refreshRepairParts();
      toast.success(t("stock.repairPartAdded"));

      return {
        id: insertedData.id,
        name: insertedData.name,
        description: insertedData.description || undefined,
        cost: insertedData.cost,
        stockQuantity: insertedData.stock_quantity,
        minStockLevel: insertedData.min_stock_level,
        isActive: insertedData.is_active,
        repairShopId: insertedData.repair_shop_id,
        createdAt: new Date(insertedData.created_at),
        updatedAt: new Date(insertedData.updated_at),
      };
    } catch (error) {
      console.error("Error adding repair part:", error);
      toast.error(t("stock.addError"));
      throw error;
    }
  };

  const updateRepairPart = async (
    id: string,
    data: Partial<RepairPartFormData>
  ): Promise<void> => {
    try {
      const updateData: any = {};
      if (data.name !== undefined) updateData.name = data.name;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.cost !== undefined) updateData.cost = data.cost;
      if (data.stockQuantity !== undefined) updateData.stock_quantity = data.stockQuantity;
      if (data.minStockLevel !== undefined) updateData.min_stock_level = data.minStockLevel;
      if (data.isActive !== undefined) updateData.is_active = data.isActive;

      const { error } = await supabase
        .from("repair_parts")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      await refreshRepairParts();
      toast.success(t("stock.repairPartUpdated"));
    } catch (error) {
      console.error("Error updating repair part:", error);
      toast.error(t("stock.updateError"));
      throw error;
    }
  };

  const deleteRepairPart = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from("repair_parts")
        .delete()
        .eq("id", id);

      if (error) throw error;

      await refreshRepairParts();
      toast.success(t("stock.repairPartDeleted"));
    } catch (error) {
      console.error("Error deleting repair part:", error);
      toast.error(t("stock.deleteError"));
      throw error;
    }
  };

  const getRepairPartById = async (id: string): Promise<RepairPart | undefined> => {
    try {
      const { data, error } = await supabase
        .from("repair_parts")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        name: data.name,
        description: data.description || undefined,
        cost: data.cost,
        stockQuantity: data.stock_quantity,
        minStockLevel: data.min_stock_level,
        isActive: data.is_active,
        repairShopId: data.repair_shop_id,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      };
    } catch (error) {
      console.error("Error fetching repair part:", error);
      return undefined;
    }
  };

  const adjustStock = async (
    partId: string,
    quantity: number,
    reason: string
  ): Promise<void> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      const { error } = await supabase
        .from("repair_parts")
        .update({
          stock_quantity: quantity,
        })
        .eq("id", partId);

      if (error) throw error;

      await refreshRepairParts();
      toast.success(t("stock.stockAdjusted"));
    } catch (error) {
      console.error("Error adjusting stock:", error);
      toast.error(t("stock.adjustError"));
      throw error;
    }
  };

  const value: RepairPartsContextType = {
    repairParts,
    loading,
    addRepairPart,
    updateRepairPart,
    deleteRepairPart,
    getRepairPartById,
    adjustStock,
    refreshRepairParts,
  };

  return (
    <RepairPartsContext.Provider value={value}>
      {children}
    </RepairPartsContext.Provider>
  );
};

export const useRepairParts = () => {
  const context = useContext(RepairPartsContext);
  if (context === undefined) {
    throw new Error("useRepairParts must be used within a RepairPartsProvider");
  }
  return context;
};

